import { useEffect } from "react";

export interface HowToStep {
  name: string;
  text: string;
  image?: string;
  url?: string;
}

interface HowToSchemaProps {
  name: string;
  description: string;
  image?: string;
  estimatedCost?: {
    currency: string;
    value: string;
  };
  totalTime?: string; // ISO 8601 duration format (e.g., "PT2H" for 2 hours, "P3D" for 3 days)
  supply?: string[];
  tool?: string[];
  steps: HowToStep[];
}

export function HowToSchema({
  name,
  description,
  image,
  estimatedCost,
  totalTime,
  supply,
  tool,
  steps,
}: HowToSchemaProps) {
  useEffect(() => {
    const schema = {
      "@context": "https://schema.org",
      "@type": "HowTo",
      "name": name,
      "description": description,
      ...(image && { "image": image }),
      ...(estimatedCost && {
        "estimatedCost": {
          "@type": "MonetaryAmount",
          "currency": estimatedCost.currency,
          "value": estimatedCost.value
        }
      }),
      ...(totalTime && { "totalTime": totalTime }),
      ...(supply && supply.length > 0 && {
        "supply": supply.map(item => ({
          "@type": "HowToSupply",
          "name": item
        }))
      }),
      ...(tool && tool.length > 0 && {
        "tool": tool.map(item => ({
          "@type": "HowToTool",
          "name": item
        }))
      }),
      "step": steps.map((step, index) => ({
        "@type": "HowToStep",
        "position": index + 1,
        "name": step.name,
        "text": step.text,
        ...(step.image && { "image": step.image }),
        ...(step.url && { "url": step.url })
      }))
    };

    // Create or update the schema script tag
    let scriptTag = document.querySelector('script[data-schema="how-to"]');
    if (!scriptTag) {
      scriptTag = document.createElement('script');
      scriptTag.setAttribute('type', 'application/ld+json');
      scriptTag.setAttribute('data-schema', 'how-to');
      document.head.appendChild(scriptTag);
    }
    scriptTag.textContent = JSON.stringify(schema);

    return () => {
      // Clean up on unmount
      const tag = document.querySelector('script[data-schema="how-to"]');
      if (tag) {
        tag.remove();
      }
    };
  }, [name, description, image, estimatedCost, totalTime, supply, tool, steps]);

  return null;
}
